import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminPortalLayout from './AdminPortalLayout';
import {
  AdminMe, RecipeRow, RecipeDetail, fetchRecipes, fetchRecipeDetail,
  unpublishRecipe, publishRecipe,
} from '../lib/admin-portal-api';
import {
  FaClipboardList, FaExclamationTriangle, FaChevronDown, FaChevronRight, FaFire,
} from '../lib/icons';

interface Props { me: AdminMe }

export default function AdminPortalRecipes({ me }: Props) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<RecipeRow[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [details, setDetails] = useState<Record<number, RecipeDetail>>({});
  const [loadingDetail, setLoadingDetail] = useState<number | null>(null);

  const load = async (pageNum = 1, query = q, append = false) => {
    if (!append) setLoading(true);
    try {
      const res = await fetchRecipes(query, pageNum, 20);
      setRows((prev) => append ? [...prev, ...res.data] : res.data);
      setPagination(res.pagination);
      setPage(pageNum);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1, ''); }, []);

  const onSearch = (e: React.FormEvent) => { e.preventDefault(); load(1, q.trim()); };

  const toggleExpand = async (r: RecipeRow) => {
    if (expanded === r.id) { setExpanded(null); return; }
    setExpanded(r.id);
    if (!details[r.id]) {
      setLoadingDetail(r.id);
      try {
        const d = await fetchRecipeDetail(r.id);
        setDetails((m) => ({ ...m, [r.id]: d }));
      } catch {
        // ignore — banner will show empty state
      } finally {
        setLoadingDetail((c) => (c === r.id ? null : c));
      }
    }
  };

  const onPublishToggle = async (r: RecipeRow) => {
    setBusy(r.id);
    try {
      if (r.publishedAt) await unpublishRecipe(r.id);
      else await publishRecipe(r.id);
      await load(page, q);
    } catch { alert('Action failed'); }
    finally { setBusy(null); }
  };

  return (
    <AdminPortalLayout me={me} active="recipes">
      <Helmet><title>Recipes — EatMeat Admin</title></Helmet>

      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Recipes</h1>
        <p className="text-sm text-gray-500 mt-1">Search the recipe library; publish or unpublish from here. Edit full content in Strapi admin.</p>
      </div>

      <form onSubmit={onSearch} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 mb-4 flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipe title…"
          className="flex-1 px-4 py-2 rounded-xl bg-gray-50 border border-gray-200 text-sm focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none" />
        <button type="submit" disabled={loading}
          className="px-5 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
          {loading ? 'Searching…' : 'Search'}
        </button>
        {q && <button type="button" onClick={() => { setQ(''); load(1, ''); }} className="px-3 py-2 text-xs text-gray-500 hover:text-gray-700">Clear</button>}
      </form>

      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 inline-flex items-start gap-2 mb-4">
          <FaExclamationTriangle className="mt-0.5 text-red-500 shrink-0" /> {error}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-20 bg-white rounded-xl animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
          <FaClipboardList className="text-4xl mx-auto mb-3 text-gray-200" />
          <p className="text-sm text-gray-500">No recipes match.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="divide-y divide-gray-100">
            {rows.map((r) => {
              const isOpen = expanded === r.id;
              const detail = details[r.id];
              return (
                <div key={r.id}>
                  <div
                    onClick={() => toggleExpand(r)}
                    className="p-4 flex items-center gap-4 hover:bg-gray-50 cursor-pointer">
                    <div className="text-gray-300 shrink-0">
                      {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                    </div>
                    {r.image_url ? (
                      <img src={r.image_url.startsWith('http') ? r.image_url : `${process.env.REACT_APP_STRAPI_API_URL || ''}${r.image_url}`}
                        alt="" className="w-14 h-14 rounded-lg object-cover bg-gray-100 shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-gray-100 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-gray-800 truncate">{r.title || '(untitled)'}</div>
                        {r.publishedAt ? (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700">Published</span>
                        ) : (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Draft</span>
                        )}
                        {r.category?.name && <span className="text-[10px] text-gray-500">{r.category.name}</span>}
                      </div>
                      <div className="text-xs text-gray-500 truncate mt-0.5">{r.description}</div>
                      <div className="text-[10px] text-gray-400 mt-1 flex gap-3">
                        <span>👍 {r.thumbs_up_count}</span>
                        <span>👎 {r.thumbs_down_count}</span>
                        {r.cooking_time && <span>⏱ {r.cooking_time}</span>}
                        {r.servings && <span>🍽 {r.servings}</span>}
                        <span>· {new Date(r.updatedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="shrink-0 flex gap-2 items-center" onClick={(e) => e.stopPropagation()}>
                      {me.isSuperAdmin && (
                        <button disabled={busy === r.id} onClick={() => onPublishToggle(r)}
                          className={`text-xs px-3 py-1.5 rounded-md font-semibold disabled:opacity-50 ${
                            r.publishedAt ? 'bg-yellow-50 text-yellow-800 hover:bg-yellow-100'
                                          : 'bg-primary text-white hover:opacity-90'
                          }`}>
                          {r.publishedAt ? 'Unpublish' : 'Publish'}
                        </button>
                      )}
                      <a href={`${process.env.REACT_APP_STRAPI_API_URL || ''}/admin/content-manager/collectionType/api::recipe.recipe/${r.documentId || r.id}`}
                        target="_blank" rel="noreferrer"
                        className="text-xs px-3 py-1.5 rounded-md bg-gray-50 text-gray-700 hover:bg-gray-100 font-semibold">
                        Edit →
                      </a>
                    </div>
                  </div>

                  {isOpen && (
                    <RecipePreview
                      row={r}
                      detail={detail}
                      loading={loadingDetail === r.id}
                    />
                  )}
                </div>
              );
            })}
          </div>
          {pagination && (
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
              <span>Showing {rows.length} of {pagination.total}</span>
              {page < pagination.pageCount && (
                <button onClick={() => load(page + 1, q, true)}
                  className="px-3 py-1.5 rounded-md bg-gray-50 hover:bg-gray-100 font-semibold text-gray-700">
                  Load more
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </AdminPortalLayout>
  );
}

/* ---------- Inline preview ---------- */
function RecipePreview({ row, detail, loading }: { row: RecipeRow; detail: any; loading: boolean }) {
  const imgUrl = row.image_url
    ? (row.image_url.startsWith('http') ? row.image_url : `${process.env.REACT_APP_STRAPI_API_URL || ''}${row.image_url}`)
    : null;

  return (
    <div className="bg-gray-50 px-6 py-5 border-t border-gray-100">
      {loading && !detail ? (
        <div className="text-sm text-gray-400 py-6 text-center">Loading preview…</div>
      ) : !detail ? (
        <div className="text-sm text-gray-400 py-6 text-center">Preview unavailable.</div>
      ) : (
        <div className="grid md:grid-cols-3 gap-5">
          {/* Left: image + meta */}
          <div className="md:col-span-1 space-y-3">
            {imgUrl ? (
              <img src={imgUrl} alt={row.title}
                className="w-full rounded-xl object-cover bg-white border border-gray-100 aspect-square" />
            ) : (
              <div className="w-full rounded-xl bg-white border border-gray-100 aspect-square flex items-center justify-center text-gray-300">
                <FaClipboardList className="text-4xl" />
              </div>
            )}
            <div className="bg-white rounded-xl border border-gray-100 p-3 text-xs space-y-1">
              {detail.cooking_time && <Meta label="Cooking time" value={detail.cooking_time} />}
              {detail.servings && <Meta label="Servings" value={String(detail.servings)} />}
              {detail.category?.name && <Meta label="Category" value={detail.category.name} />}
              {detail.subcategory?.name && <Meta label="Sub-category" value={detail.subcategory.name} />}
              <Meta label="Engagement" value={`👍 ${detail.thumbs_up_count} · 👎 ${detail.thumbs_down_count}`} />
            </div>

            {detail.nutrients?.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 p-3">
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 inline-flex items-center gap-1.5"><FaFire className="text-orange-500" /> Nutrients</div>
                <div className="grid grid-cols-2 gap-1.5 text-xs">
                  {detail.nutrients.map((n: any) => (
                    <div key={n.id} className="flex justify-between gap-2">
                      <span className="text-gray-500 truncate">{n.label || '—'}</span>
                      <span className="font-semibold text-gray-700">{n.value || '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: description + ingredients + instructions */}
          <div className="md:col-span-2 space-y-4">
            {detail.description && (
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Description</div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{detail.description}</p>
              </div>
            )}

            {detail.ingredients?.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Ingredients · {detail.ingredients.length}</div>
                <ul className="text-sm space-y-1.5">
                  {detail.ingredients.map((i: any) => (
                    <li key={i.id} className="flex gap-2">
                      <span className="text-gray-300">•</span>
                      <span className="text-gray-700">
                        {i.quantity ? <span className="font-semibold">{i.quantity}{i.unit ? ` ${i.unit}` : ''}</span> : null}
                        {i.quantity ? ' ' : null}
                        {i.name || '(unnamed)'}
                        {i.notes && <span className="text-gray-400 text-xs"> — {i.notes}</span>}
                        {i.isReplaceable && <span className="ml-2 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">replaceable</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {detail.instructions && (
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Instructions</div>
                <Instructions blocks={detail.instructions} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-gray-400">{label}</span>
      <span className="font-semibold text-gray-700 text-right truncate">{value}</span>
    </div>
  );
}

/* Render Strapi 5 "blocks" field as plain text. Supports paragraph + list (ordered/unordered). */
function Instructions({ blocks }: { blocks: any }) {
  if (!blocks || !Array.isArray(blocks) || blocks.length === 0) {
    return <p className="text-sm text-gray-400">No instructions.</p>;
  }
  const renderInline = (children: any[]): string =>
    (children || []).map((c) => c?.text || '').join('');

  return (
    <ol className="text-sm space-y-2 list-decimal pl-5">
      {blocks.map((b: any, i: number) => {
        if (b.type === 'list' && Array.isArray(b.children)) {
          return b.children.map((li: any, j: number) => (
            <li key={`${i}-${j}`} className="text-gray-700">{renderInline(li.children)}</li>
          ));
        }
        if (b.type === 'paragraph') {
          return <li key={i} className="text-gray-700">{renderInline(b.children) || <span className="text-gray-300">(empty)</span>}</li>;
        }
        if (b.type === 'heading') {
          return <li key={i} className="text-gray-800 font-semibold">{renderInline(b.children)}</li>;
        }
        return <li key={i} className="text-gray-700">{renderInline(b.children || [])}</li>;
      })}
    </ol>
  );
}
